<template>
<div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div class="row justify-content-center">
        <div class="col-sm-12 col-xs-12">
            <div class="card-header pb-0 col-sm-12 d-flex mainDiv dashboard-div">
                <div class="col-sm-12 col-xs-12">
                    <div class="titleDiv CertListTitle dashboard-title-row">
                        <div class="dashboard-title-text">
                            <h3>{{ $t('dashboard_title') }}</h3>
                            <h5>{{ $t('dashboard_desc') }}</h5>
                        </div>
                        <div class="dashboard-header-boxes">
                            <div class="column-box user-guide-image dashboard-header-banner">
                                <a href="https://certilife.oopy.io/" target="blank">
                                    <img src="../assets/img/Banner_UserGuide.png"  />
                                </a>
                            </div>
                            <div class="column-box dashboard-notice-box dashboard-header-notice">
                                <h3>{{ $t('dashboard_noti_title') }}</h3>
                                <ul>
                                    <li
                                        v-for="item in noticeList"
                                        :key="item._id"
                                        style="cursor: pointer"
                                        @click="routeToPost(item._id)"
                                    >
                                        {{ item.title }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <DashboardPeriodFilter
                        :initial-period="initialPrefs.period"
                        :downloading="downloading"
                        @change="handlePeriodChange"
                        @download="handleDownload"
                    />

                    <div class="dashboard-widgets">
                        <div class="dashboard-row">
                            <SummaryCards
                                :total-count="summary.totalCount"
                                :today-count="summary.todayCount"
                                :loading="summaryLoading"
                                :error="summaryError"
                                @retry="fetchSummary"
                            />
                        </div>
                        <div class="dashboard-row">
                            <DemographicsChart
                                :buckets="demographics.buckets"
                                :unknown-count="demographics.unknown.count"
                                :total-count="demographics.totalCount"
                                :mode="demographicsMode"
                                :bucket-size="demographicsBucketSize"
                                :exclude-unknown="demographicsExcludeUnknown"
                                :loading="demographicsLoading"
                                :error="demographicsError"
                                :is-empty="demographicsEmpty"
                                :empty-message="$t('dashboard_widget_empty')"
                                @retry="fetchDemographics"
                                @params-change="handleDemographicsParamsChange"
                                @exclude-unknown-change="handleDemographicsExcludeUnknownChange"
                            />
                        </div>
                        <div class="dashboard-row">
                            <TrendWidget
                                :buckets="trend.buckets"
                                :total-count="trend.totalCount"
                                :compare-total-count="trend.compareTotalCount"
                                :granularity="trendGranularity"
                                :compare="trendCompare"
                                :chart-type="trendChartType"
                                :is-all-period="isAllPeriod"
                                :period-day-count="periodDayCount"
                                :loading="trendLoading"
                                :error="trendError"
                                :is-empty="trendEmpty"
                                :empty-message="$t('dashboard_widget_empty')"
                                @retry="fetchTrend"
                                @params-change="handleTrendParamsChange"
                                @chart-type-change="handleTrendChartTypeChange"
                            />
                        </div>
                        <div class="dashboard-row dashboard-row--split">
                            <RankingWidget
                                :title="$t('dashboard_nfts_hospital')"
                                :items="hospitalItems"
                                :loading="rankingsLoading"
                                :error="rankingsError"
                                :is-empty="rankingsEmpty"
                                :empty-message="`${$t('dashboard_nfts_hospital_no_result_1')} ${$t('dashboard_nfts_hospital_no_result_2')}`"
                                @retry="fetchRankings"
                            />
                            <RankingWidget
                                :title="$t('dashboard_nfts_product')"
                                :header-note="productHeaderNote"
                                :items="productItems"
                                show-percentage
                                :loading="rankingsLoading"
                                :error="rankingsError"
                                :is-empty="rankingsEmpty"
                                :empty-message="`${$t('dashboard_nfts_propduct_no_result_1')} ${$t('dashboard_nfts_propduct_no_result_2')}`"
                                @retry="fetchRankings"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>
<script>
import { getAccessToken, getProfile } from '@/utils/tokenStore';
import { masterCheckUsername } from '@/utils/supportMaster';
import DashboardPeriodFilter from './components/dashboard/DashboardPeriodFilter.vue';
import SummaryCards from './components/dashboard/SummaryCards.vue';
import RankingWidget from './components/dashboard/RankingWidget.vue';
import DemographicsChart from './components/dashboard/DemographicsChart.vue';
import TrendWidget from './components/dashboard/TrendWidget.vue';
import { formatCommaNumber, jwtDecoder, oneBtnDialog } from '@/utils/utils';
import { loadDashboardPrefs, saveDashboardPrefs, MAX_DAILY_BUCKETS } from '@/utils/dashboard-prefs';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

export default {
    name: 'Dashboard',
    components: {
        DashboardPeriodFilter,
        SummaryCards,
        RankingWidget,
        DemographicsChart,
        TrendWidget,
    },
    data() {
        // localStorage 하이드레이션 — 자식 컴포넌트(DashboardPeriodFilter 등)가 마운트되기 전에
        // 이 값들이 준비되어야 최초 1회 emit만으로 올바른 파라미터를 조회한다(기본값→재조회 이중 fetch 방지).
        const initialPrefs = loadDashboardPrefs();
        return {
            formatCommaNumber,
            companyName: '',
            noticeList: [],
            period: null,
            initialPrefs,

            summary: { totalCount: 0, todayCount: 0 },
            summaryLoading: false,
            summaryError: false,

            // M5 발행 추이 위젯. 기본값(신규 사용자) = 월별 + 전년 동기 + 막대, localStorage에 저장된
            // 값이 있으면(가드 보정 후) 그 값으로 시작한다.
            trend: { totalCount: 0, compareTotalCount: null, buckets: [] },
            trendLoading: false,
            trendError: false,
            trendGranularity: initialPrefs.trend.granularity,
            trendCompare: initialPrefs.trend.compare,
            trendChartType: initialPrefs.trend.chartType,
            trendRequestSeq: 0,

            rankings: { totalCount: 0, hospitals: [], products: [] },
            rankingsLoading: false,
            rankingsError: false,

            demographics: { totalCount: 0, buckets: [], unknown: { count: 0 } },
            demographicsLoading: false,
            demographicsError: false,
            // 분포 위젯 집계 기준/단위. 기본값(신규 사용자) = 발행 당시 나이/5년, localStorage 값 우선.
            demographicsMode: initialPrefs.demographics.mode,
            demographicsBucketSize: initialPrefs.demographics.bucketSize,
            // '미상 제외' 체크박스 초기값(기본 체크됨). localStorage 값 우선.
            demographicsExcludeUnknown: initialPrefs.demographics.excludeUnknown,

            // 기간 연속 전환 시 레이스 가드(latest-request-wins). 위젯별 최신 요청 순번만 반영.
            summaryRequestSeq: 0,
            rankingsRequestSeq: 0,
            demographicsRequestSeq: 0,

            // REQ-1/REQ-2: 엑셀 다운로드 진행 플래그(E-2.2 중복 클릭 방지 + 버튼 disabled/loading).
            downloading: false,
        };
    },
    computed: {
        // REQ-MOD-3 U-3.4: companyName 없는 건 "미지정"
        hospitalItems() {
            return this.rankings.hospitals.map((item) => {
                if (item.isEtc) {
                    // etcGroupCount(잔여 병원 수)가 있으면 "그 외 N개 병원 · 건수"로 병기, 값 칸은 중복 방지로 비운다.
                    const name = item.etcGroupCount
                        ? this.$i18n.t('dashboard_ranking_etc_hospital_detail', {
                            count: formatCommaNumber(item.etcGroupCount),
                            total: formatCommaNumber(item.count),
                        })
                        : this.$i18n.t('dashboard_ranking_etc');
                    return {
                        key: 'etc',
                        name,
                        count: item.count,
                        isEtc: true,
                        hideValue: !!item.etcGroupCount,
                    };
                }
                return {
                    key: item.companyId,
                    name: item.name || this.$i18n.t('dashboard_ranking_unnamed'),
                    imgUrl: item.imgUrl,
                    count: item.count,
                    isEtc: false,
                };
            });
        },
        productItems() {
            return this.rankings.products.map((item) => {
                if (item.isEtc) {
                    return {
                        key: 'etc',
                        name: this.$i18n.t('dashboard_ranking_etc'),
                        count: item.count,
                        percentage: item.percentage,
                        isEtc: true,
                    };
                }
                return {
                    key: item.itemName,
                    name: item.itemName || this.$i18n.t('dashboard_ranking_unnamed'),
                    count: item.count,
                    percentage: item.percentage,
                    isEtc: false,
                };
            });
        },
        productHeaderNote() {
            return this.$i18n.t('dashboard_ranking_product_header_note', { count: formatCommaNumber(this.rankings.totalCount) });
        },
        rankingsEmpty() {
            return !this.rankingsLoading && !this.rankingsError && this.rankings.totalCount === 0;
        },
        // QA 버그 수정: totalCount>0이어도 buckets가 비어있고(전량 미상) 그릴 막대가
        // 하나도 없는 경우(미상 제외 체크 상태, 또는 unknown.count도 0)까지 빈 상태로 처리한다.
        // (미상 제외 해제 + unknown.count>0이면 미상 막대 1개는 그릴 수 있으므로 빈 상태 아님)
        demographicsEmpty() {
            if (this.demographicsLoading || this.demographicsError) return false;
            if (this.demographics.totalCount === 0) return true;
            const hasNoBuckets = this.demographics.buckets.length === 0;
            const hasNoDrawableUnknown = this.demographicsExcludeUnknown || this.demographics.unknown.count === 0;
            return hasNoBuckets && hasNoDrawableUnknown;
        },
        trendEmpty() {
            return !this.trendLoading && !this.trendError && this.trend.totalCount === 0;
        },
        // TrendWidget의 클라이언트 가드(일별 상한 400·비교 가능 여부) 판단에 필요한 기간 정보.
        isAllPeriod() {
            return !this.period || (!this.period.startAt && !this.period.endAt);
        },
        periodDayCount() {
            if (this.isAllPeriod) return 0;
            return dayjs(this.period.endAt, 'YYYYMMDD').diff(dayjs(this.period.startAt, 'YYYYMMDD'), 'day');
        },
    },
    async mounted() {
        await Promise.allSettled([
            this.getCompanyInfo(),
            this.getPopupNotice(),
            this.fetchNoticeList(),
        ]);
    },
    methods: {
        // REQ-MOD-1 E-2.2: 기간 칩 변경 시 전 위젯을 병렬로 갱신(위젯별 실패 격리, S-2.1)
        handlePeriodChange(period) {
            if (!period) return; // N-1.1: 직접선택 범위가 아직 완성되지 않음
            this.period = period;
            this.savePrefs();
            this.fetchSummary();
            this.fetchTrend();
            this.fetchRankings();
            this.fetchDemographics();
        },
        periodParams() {
            if (!this.period || (!this.period.startAt && !this.period.endAt)) return {};
            return { startAt: this.period.startAt, endAt: this.period.endAt };
        },
        // 현재 컨트롤 상태를 localStorage에 저장한다(각 컨트롤의 커밋 시점마다 호출, 디바운스 불필요).
        savePrefs() {
            const periodPref = this.period && this.period.filter === 'etc'
                ? { filter: 'etc', startAt: this.period.startAt, endAt: this.period.endAt }
                : { filter: (this.period && this.period.filter) || 'all' };
            saveDashboardPrefs({
                period: periodPref,
                demographics: {
                    mode: this.demographicsMode,
                    bucketSize: this.demographicsBucketSize,
                    excludeUnknown: this.demographicsExcludeUnknown,
                },
                trend: { granularity: this.trendGranularity, compare: this.trendCompare, chartType: this.trendChartType },
            });
        },
        // 분포 위젯의 집계 기준(년도/나이)·단위(1/5/10년) 컨트롤 변경 → 최신 파라미터로 재조회.
        // fetchDemographics의 requestSeq 가드가 호출마다 갱신되므로 period 전환과 동일하게
        // 느린 응답의 덮어쓰기가 자동으로 차단된다(별도 처리 불필요).
        handleDemographicsParamsChange({ mode, bucketSize }) {
            this.demographicsMode = mode;
            this.demographicsBucketSize = bucketSize;
            this.savePrefs();
            this.fetchDemographics();
        },
        // [HARD] E-3.1/E-3.3: '미상 제외' 토글은 영속화 전용 채널이며 demographics API를
        // 재조회하지 않는다(handleDemographicsParamsChange와 분리된 별도 핸들러).
        handleDemographicsExcludeUnknownChange(excludeUnknown) {
            this.demographicsExcludeUnknown = excludeUnknown;
            this.savePrefs();
        },
        // 추이 위젯의 단위(일/월/년)·비교(없음/직전/전년) 컨트롤 변경 → 최신 파라미터로 재조회.
        handleTrendParamsChange({ granularity, compare }) {
            this.trendGranularity = granularity;
            this.trendCompare = compare;
            this.savePrefs();
            this.fetchTrend();
        },
        // 형태(막대/꺾은선)는 API 파라미터가 아니므로 재조회는 필요 없고 영속화만 한다.
        handleTrendChartTypeChange(chartType) {
            this.trendChartType = chartType;
            this.savePrefs();
        },
        // REQ-1/REQ-2: 대시보드 엑셀 다운로드. E-2.2 중복 클릭 방지, 실패 시 중단(불완전 파일 미생성).
        // 수신자·추이 시트는 화면 위젯 상태(mode/bucketSize/granularity)와 무관하게 다운로드
        // 시점에 최소 단위 로우데이터를 별도 조회한다(순위 전체 조회와 병렬).
        async handleDownload() {
            if (this.downloading) return;
            this.downloading = true;
            try {
                // TrendWidget.dailyDisabled와 동일 규칙(전체 기간이거나 400일 초과 시 일별 불가) —
                // 불가한 경우 월별로 폴백해 조회한다.
                const dailyAllowed = !(this.isAllPeriod || this.periodDayCount > MAX_DAILY_BUCKETS);
                const trendGranularityForExport = dailyAllowed ? 'day' : 'month';

                let fullRankings;
                let rawDemographics;
                let rawTrend;
                try {
                    const [rankingsRes, demographicsRes, trendRes] = await Promise.all([
                        this.$apiInstance.get('/companies/manufacturer/dashboard/rankings', {
                            params: { ...this.periodParams(), full: 'true' },
                        }),
                        this.$apiInstance.get('/companies/manufacturer/dashboard/demographics', {
                            params: { ...this.periodParams(), mode: 'birthYear', bucketSize: 1 },
                        }),
                        this.$apiInstance.get('/companies/manufacturer/dashboard/trend', {
                            params: { ...this.periodParams(), granularity: trendGranularityForExport, compare: 'none' },
                        }),
                    ]);
                    fullRankings = {
                        hospitals: rankingsRes.data.hospitals || [],
                        products: rankingsRes.data.products || [],
                    };
                    rawDemographics = {
                        buckets: demographicsRes.data.buckets || [],
                        unknown: demographicsRes.data.unknown || { count: 0 },
                    };
                    rawTrend = {
                        buckets: trendRes.data.buckets || [],
                        granularity: trendGranularityForExport,
                    };
                } catch (error) {
                    console.error(error);
                    // N-4.1: 셋 중 하나라도 실패하면 불완전 파일을 저장하지 않고 다운로드를 중단한다.
                    await oneBtnDialog('엑셀 다운로드 실패', '데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.', false);
                    return;
                }
                this.buildAndDownloadWorkbook(fullRankings, rawDemographics, rawTrend);
            } finally {
                this.downloading = false;
            }
        },
        // 조회기간 표시 문자열("전체" 또는 "YYYY-MM-DD~YYYY-MM-DD"). periodParams()와 동일 판단 기준.
        periodLabel() {
            if (!this.period || (!this.period.startAt && !this.period.endAt)) return '전체';
            return `${dayjs(this.period.startAt, 'YYYYMMDD').format('YYYY-MM-DD')}~${dayjs(this.period.endAt, 'YYYYMMDD').format('YYYY-MM-DD')}`;
        },
        // localStorage CompanyInfo에서 제조사명을 안전하게 읽는다(손상/부재 시 '제조사' 폴백).
        companyDisplayName() {
            try {
                const raw = getProfile('CompanyInfo');
                if (!raw) return this.companyName || '제조사';
                const parsed = JSON.parse(raw);
                return (parsed && parsed.name) || this.companyName || '제조사';
            } catch (error) {
                return this.companyName || '제조사';
            }
        },
        // 요약 시트: 조회기간·총 발행 인증서 수·당일 발행 수·다운로드 일시(E-4.1: 오류 시 '조회 실패' 한 행).
        buildSummarySheetRows() {
            if (this.summaryError) return [{ 항목: '요약', 값: '조회 실패' }];
            return [
                { 항목: '조회기간', 값: this.periodLabel() },
                { 항목: '총 발행 인증서 수', 값: this.summary.totalCount },
                { 항목: '당일 발행 수', 값: this.summary.todayCount },
                { 항목: '다운로드 일시', 값: dayjs().format('YYYY-MM-DD HH:mm:ss') },
            ];
        },
        // 병원별 발행 시트: 순위 전체 조회 결과를 건수 내림차순 전체 순위로 개별 행 전개(U-2.2, etc 묶음 없음).
        buildHospitalSheetRows(hospitals) {
            if (!hospitals.length) return [{ 순위: '', 병원명: '데이터 없음', '발행 건수': '' }];
            return hospitals.map((item, index) => ({
                순위: index + 1,
                병원명: item.name || '미지정',
                '발행 건수': item.count,
            }));
        },
        // 제품별 발행 시트: 순위 전체 조회 결과를 개별 행으로 전개, 비율(%)은 백엔드 계산값을 소수 1자리로 표기.
        buildProductSheetRows(products) {
            if (!products.length) return [{ 순위: '', 제품명: '데이터 없음', '발행 건수': '', '비율(%)': '' }];
            return products.map((item, index) => ({
                순위: index + 1,
                제품명: item.itemName || '미지정',
                '발행 건수': item.count,
                '비율(%)': typeof item.percentage === 'number' ? Number(item.percentage.toFixed(1)) : '',
            }));
        },
        // 수신자 출생연도×성별 시트(로우데이터): 화면 mode/bucketSize/미상 제외 설정과 무관하게
        // 다운로드 시점에 birthYear·1년 단위로 별도 조회한 원본을 롱 포맷(연도×성별)으로 전개한다.
        // 퍼센트·분모 안내 행은 포함하지 않는다(가공 없는 원시 건수만 기록).
        buildDemographicsSheetRows(rawDemographics) {
            const rows = [];
            rawDemographics.buckets.forEach((bucket) => {
                rows.push({ 출생연도: bucket.bucketLabel, 성별: '남성', 건수: bucket.male || 0 });
                rows.push({ 출생연도: bucket.bucketLabel, 성별: '여성', 건수: bucket.female || 0 });
            });
            // 미상 행은 화면 상태와 무관하게 항상 포함한다(정보 손실 없음).
            rows.push({ 출생연도: '미상', 성별: '-', 건수: rawDemographics.unknown.count || 0 });
            return rows;
        },
        // 발행 추이 시트(로우데이터): 화면 granularity/compare 설정과 무관하게 다운로드 시점에
        // 일별(400일 초과 또는 전체 기간이면 월별로 폴백)로 별도 조회한 원본을 그대로 기록한다.
        // 비교 컬럼은 없다(compare: 'none' 고정 조회).
        buildTrendSheetRows(rawTrend) {
            const headerLabel = rawTrend.granularity === 'month' ? '월' : '일자';
            return rawTrend.buckets.map((bucket) => ({
                [headerLabel]: this.formatRawTrendBucketLabel(bucket.currentStart, rawTrend.granularity),
                '발행 건수': bucket.current,
            }));
        },
        // 발행 추이 로우데이터 구간 라벨 — 조회 시점 granularity(일/월) 기준으로 날짜를 표기한다.
        formatRawTrendBucketLabel(dateStr, granularity) {
            if (!dateStr) return '';
            const d = dayjs(dateStr);
            return granularity === 'month' ? d.format('YYYY-MM') : d.format('YYYY-MM-DD');
        },
        // 5개 시트를 단일 워크북으로 조립해 저장한다(DailyStatistics.vue 패턴 재사용).
        buildAndDownloadWorkbook(fullRankings, rawDemographics, rawTrend) {
            const wb = XLSX.utils.book_new();

            const summaryWs = XLSX.utils.json_to_sheet(this.buildSummarySheetRows());
            XLSX.utils.book_append_sheet(wb, summaryWs, '요약');

            const hospitalWs = XLSX.utils.json_to_sheet(this.buildHospitalSheetRows(fullRankings.hospitals));
            XLSX.utils.book_append_sheet(wb, hospitalWs, '병원별 발행');

            const productWs = XLSX.utils.json_to_sheet(this.buildProductSheetRows(fullRankings.products));
            XLSX.utils.book_append_sheet(wb, productWs, '제품별 발행');

            const demographicsWs = XLSX.utils.json_to_sheet(this.buildDemographicsSheetRows(rawDemographics));
            XLSX.utils.book_append_sheet(wb, demographicsWs, '수신자 출생연도x성별');

            const trendWs = XLSX.utils.json_to_sheet(this.buildTrendSheetRows(rawTrend));
            XLSX.utils.book_append_sheet(wb, trendWs, '발행 추이');

            const fileName = `${this.companyDisplayName()}_대시보드_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
            XLSX.writeFile(wb, fileName);
        },
        async fetchSummary() {
            // latest-request-wins: 이 호출 시작 시점의 순번을 캡처하고, 응답 도착 시 순번이
            // 앞서갔으면(더 최신 요청이 이미 시작됨) 느린 응답의 데이터/에러/로딩 갱신을 모두 무시한다.
            const requestSeq = (this.summaryRequestSeq += 1);
            this.summaryLoading = true;
            this.summaryError = false;
            try {
                const response = await this.$apiInstance.get('/companies/manufacturer/dashboard/summary', {
                    params: this.periodParams(),
                });
                if (requestSeq !== this.summaryRequestSeq) return;
                this.summary = {
                    totalCount: response.data.totalCount || 0,
                    todayCount: response.data.todayCount || 0,
                };
            } catch (error) {
                console.log(error);
                if (requestSeq !== this.summaryRequestSeq) return;
                this.summaryError = true;
            } finally {
                if (requestSeq === this.summaryRequestSeq) this.summaryLoading = false;
            }
        },
        async fetchRankings() {
            const requestSeq = (this.rankingsRequestSeq += 1);
            this.rankingsLoading = true;
            this.rankingsError = false;
            try {
                const response = await this.$apiInstance.get('/companies/manufacturer/dashboard/rankings', {
                    params: this.periodParams(),
                });
                if (requestSeq !== this.rankingsRequestSeq) return;
                this.rankings = {
                    totalCount: response.data.totalCount || 0,
                    hospitals: response.data.hospitals || [],
                    products: response.data.products || [],
                };
            } catch (error) {
                console.log(error);
                if (requestSeq !== this.rankingsRequestSeq) return;
                this.rankingsError = true;
            } finally {
                if (requestSeq === this.rankingsRequestSeq) this.rankingsLoading = false;
            }
        },
        async fetchTrend() {
            const requestSeq = (this.trendRequestSeq += 1);
            this.trendLoading = true;
            this.trendError = false;
            try {
                const response = await this.$apiInstance.get('/companies/manufacturer/dashboard/trend', {
                    params: {
                        ...this.periodParams(),
                        granularity: this.trendGranularity,
                        compare: this.trendCompare,
                    },
                });
                if (requestSeq !== this.trendRequestSeq) return;
                this.trend = {
                    totalCount: response.data.totalCount || 0,
                    compareTotalCount: response.data.compareTotalCount ?? null,
                    buckets: response.data.buckets || [],
                };
            } catch (error) {
                console.log(error);
                if (requestSeq !== this.trendRequestSeq) return;
                this.trendError = true;
            } finally {
                if (requestSeq === this.trendRequestSeq) this.trendLoading = false;
            }
        },
        async fetchDemographics() {
            const requestSeq = (this.demographicsRequestSeq += 1);
            this.demographicsLoading = true;
            this.demographicsError = false;
            try {
                const response = await this.$apiInstance.get('/companies/manufacturer/dashboard/demographics', {
                    params: {
                        ...this.periodParams(),
                        mode: this.demographicsMode,
                        bucketSize: this.demographicsBucketSize,
                    },
                });
                if (requestSeq !== this.demographicsRequestSeq) return;
                this.demographics = {
                    totalCount: response.data.totalCount || 0,
                    buckets: response.data.buckets || [],
                    unknown: response.data.unknown || { count: 0 },
                };
            } catch (error) {
                console.log(error);
                if (requestSeq !== this.demographicsRequestSeq) return;
                this.demographicsError = true;
            } finally {
                if (requestSeq === this.demographicsRequestSeq) this.demographicsLoading = false;
            }
        },
        // 공지 목록: 레거시 단일 대시보드 응답 대신 별도 공지 API 사용
        async fetchNoticeList() {
            try {
                const response = await this.$apiInstance.get('/notices', {
                    params: { page: 1, limit: 5, target: 'MANUFACTURER' },
                });
                this.noticeList = response.data.docs || [];
            } catch (error) {
                console.log(error);
            }
        },
        async getPopupNotice(){
            function getCookie(name) {
                var nameEQ = name + "=";
                var ca = document.cookie.split(';');
                for(var i=0;i < ca.length;i++) {
                    var c = ca[i];
                    while (c.charAt(0)==' ') c = c.substring(1,c.length);
                    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
                }
                return null;
            }
            function setCookie( name, value, expiredays ) {
                    const todayDate = dayjs().add(expiredays, 'day');
                    const expires = todayDate.toDate().toUTCString();
                    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; expires=${expires};`;
            }
            try {
                const response = await this.$apiInstance.get(`/notices/popup-notice`);

                if(response.data
                && response.data.targets.includes('MANUFACTURER')
                ){
                const noShowPopupNoticeId = getCookie('no-show-popup-notice-manu');

                if(noShowPopupNoticeId == response.data._id){
                    return false;
                }
                await Swal.fire({
                    title: '공지사항',
                    html: `<div style="display:flex;flex-direction: column;height:100%;"><div class='notice-body'>${response.data.contents}</div>
                    </div>
                `,
                    buttonsStyling: false,
                    confirmButtonText: '확인',
                    cancelButtonText: '다시 보지 않기',
                    showCancelButton: true,
                    heightAuto: true,
                    customClass: {
                        popup: 'popup-notice',
                        container: 'popup-notice-container',
                        confirmButton: 'btn btn-primary-confirm btn-hover swal-button-custom',
                        cancelButton: 'btn btn-secondary-cancel btn-hover swal-button-custom',
                    }
                }).then((result) => {
                    if (result.isDismissed) {
                        setCookie("no-show-popup-notice-manu",response.data._id, 365 );
                    }
                })
                return response.data
                };
            } catch (error) {
                console.log(error);
            }
        },
        async getCompanyInfo() {
            const companyId = jwtDecoder(getAccessToken());
            try {
                const response = await this.$apiInstance.get(`/companies/${companyId}`);
                if (response.status === 200 || response.status === 201) {
                    const { data } = response;
                    this.companyName = data.name;

                    if(masterCheckUsername(data.accountInfo.username, this.$store.state.privileges) == "admin_dentiumchina"){ // 지원 탭 = 그 회사 최상위 관리자(utils/supportMaster)
                        this.$router.push('/customize/menu')
                    }
                }
            } catch (error) {
                console.log(error);
                await oneBtnDialog(this.$i18n.t('get_comanyinfo_error_title'), this.$i18n.t('get_comanyinfo_error_content'), false);
            }
        },
        routeToPost(id) {
            this.$router.push({
                name: 'NoticeRead',
                params: { id: id },
                query: { page: this.page },
            });
        },
    },
};
</script>

<style scoped>
/* 제목 행: 타이틀(좌, 가변) + 원래 우측 사이드바에 있던 사용방법 배너/공지 카드
   (우, 원형 유지). justify-content:space-between은 넓은 뷰포트에서 두 그룹을
   화면 양 끝까지 벌려놓아 박스가 제목과 분리되어 보이는 문제가 있었다 —
   대신 header-boxes 그룹 자체에 margin-left:auto를 줘 그룹 내부(배너+공지)는
   항상 붙어있고, 그룹만 우측으로 밀리도록 한다. */
.dashboard-title-row {
    align-items: flex-start;
    flex-wrap: wrap;
    row-gap: 12px;
}

.dashboard-header-boxes {
    display: flex;
    gap: 16px;
    flex-shrink: 0;
    margin-left: auto;
}

/* 두 박스는 dashboard.scss의 .column-box/.user-guide-image/.dashboard-notice-box를
   그대로 재사용(색상·테두리·라운드·타이포·이미지 원형 유지)한다. 배너는 원래
   사이드바 폭(260px)과 이미지 높이(130px)를 그대로 두어 이미지가 잘리지 않게
   하고, 공지 카드는 배너와 같은 높이(130px)로 맞춰 overflow:hidden으로 넘치는
   항목만 자연스럽게 가린다(둘 다 폭만 축소, 색상/타이포/카드 모양은 원본 그대로). */
.dashboard-header-banner {
    /* 전역 .dashboard-div .user-guide-image { width:100% } 이 스코프 규칙을 덮어써
       배너가 폭주(그룹이 컨테이너 밖으로 overflow)하던 문제 — !important로 고정 */
    width: 260px !important;
    margin-bottom: 0;
    flex-shrink: 0;
}

.dashboard-header-notice {
    width: 300px;
    height: 130px;
    margin-bottom: 0 !important;
    overflow: hidden;
    flex-shrink: 0;
}

/* 모든 위젯 행이 동일한 폭 기준(같은 부모, 동일 gap)을 공유하도록 통일한다.
   요약/순위/분포 행 간 카드 좌우 경계 불일치(그리드 폭 통일 버그)의 근본 원인은
   Bootstrap .col-* 그리드 패딩과 커스텀 gap 레이아웃이 행마다 다르게 섞여 있던 것 —
   이제 세 행 모두 Bootstrap col 클래스 없이 이 컨테이너 하나만 공유한다. */
.dashboard-widgets {
    width: 100%;
}

.dashboard-row {
    width: 100%;
}

.dashboard-row + .dashboard-row {
    margin-top: 16px;
}

.dashboard-row--split {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

/* ==========================================================================
   모바일 (767px 이하)

   이 화면에는 @media 가 하나도 없었다.
   머리말 오른쪽 두 박스가 260px + 16px + 300px = 576px 를 차지하는데
   셋 다 flex-shrink:0 이라 줄어들지도 않는다. 375px 화면에서 375px 넘게 넘쳤고
   실측에서 289px 이 잘렸다(잘린 요소 16~20개).

   폰에서는 제목 아래로 세로로 쌓고 폭 고정을 푼다. PC 는 건드리지 않는다.
   ========================================================================== */
@media screen and (max-width: 767px) {
    .dashboard-header-boxes {
        flex-direction: column;
        width: 100%;
        margin-left: 0;
        gap: 12px;
        flex-shrink: 1;
    }

    /* 배너 폭 260px 은 이미지가 잘리지 않게 잡아 둔 값이라 !important 가 붙어 있다.
       폰에서는 화면이 그보다 좁으므로 같은 강도로 되돌린다. */
    .dashboard-header-banner,
    .dashboard-header-notice {
        width: 100% !important;
        flex-shrink: 1;
    }

    /* 공지 카드는 배너와 높이를 맞추려고 130px 로 묶여 있었다.
       세로로 쌓이면 맞출 상대가 없으므로 내용에 맞춘다. */
    .dashboard-header-notice {
        height: auto;
    }

    /* 배너 이미지.

       dashboard.scss 의 `.dashboard-div .user-guide-image img` 가
       height:130px + object-fit:cover 다. PC 에서는 배너가 260×130 이라 이미지 비율과
       얼추 맞아 거의 잘리지 않았는데, 위에서 폭을 100%(≈347px)로 늘리면서 상자만 넓적해졌다.
       cover 는 넓어진 상자를 채우려고 이미지를 확대한 뒤 넘치는 위아래를 잘라 내므로
       안내 문구 윗줄과 책 그림 아랫부분이 먹혔다.

       높이 고정을 풀면 이미지가 제 비율을 지켜 잘릴 일이 없어진다.
       선택자에 조상 .dashboard-div 를 붙인 이유는 명시도 때문이다 —
       `.dashboard-header-banner img` 로 쓰면 전역 규칙과 무게가 같아(클래스2+요소1)
       순서에 밀려 적용되지 않았다. !important 대신 조상을 얹어 이긴다. */
    .dashboard-div .user-guide-image img {
        height: auto;
    }

    /* 좌우로 나뉘던 위젯 행도 세로로 쌓는다 */
    .dashboard-row--split {
        flex-direction: column;
    }
}
</style>
